/** @see packages/pixai-server/src/util/sleep.ts */

export const sleep = (ms: number, abortSignal?: AbortSignal) =>
  new Promise<void>(resolve => {
    const timeout = setTimeout(resolve, ms)
    abortSignal?.addEventListener(
      'abort',
      () => {
        clearTimeout(timeout)
        resolve()
      },
      { once: true },
    )
  })

export class TimeoutError extends Error {}

/**
 * Creates a promise that always rejects with a `TimeoutError` after the specified delay.
 *
 * This utility is designed to be used with `Promise.race()` to enforce time limits on async operations.
 * The generic type parameter `T` is only for TypeScript compatibility and does not affect runtime behavior
 * — the promise will never resolve successfully.
 *
 * @template T - The nominal type parameter for TypeScript type inference (never actually returns this type)
 * @param ms - The delay in milliseconds before throwing the timeout error
 * @param reason - Optional description of what timed out (default: 'timeout')
 * @returns A promise that always rejects after the specified delay
 *
 * @example
 * ```ts
 * try {
 *   const result: string = await Promise.race([
 *     timeout(5000, 'API call timeout'), // Inferred as `string` but never returns a string, only throws while timeout.
 *     fetchData<string>()
 *   ]);
 * } catch (err) {
 *   if (err instanceof TimeoutError) {
 *     console.error('Operation timed out');
 *   }
 * }
 * ```
 */
export const timeout = <T = void>(
  ms: number,
  reason = 'timeout',
): Promise<T> => {
  // Initialize the error with stack trace.
  // In development mode, the error is initialized immediately to capture an accurate stack trace at the call site.
  // In production, the error is created when thrown to reduce overhead.
  const err = import.meta.env.DEV ? new TimeoutError(reason) : null
  return sleep(ms).then(() => {
    throw err || new TimeoutError(reason)
  })
}

/**
 * Wraps a promise with a timeout, rejecting if the operation takes longer than the specified duration.
 *
 * This is a convenience wrapper around `Promise.race()` with `timeout()`. If the provided promise
 * resolves or rejects before the timeout, its result is returned. If the timeout expires first,
 * a `TimeoutError` is thrown.
 *
 * @template T - The type of value the promise resolves to
 * @param promise - The promise to apply a timeout to
 * @param ms - The timeout duration in milliseconds
 * @param reason - Optional description of what timed out (default: 'timeout')
 * @returns A promise that resolves/rejects with the original promise's result, or rejects with `TimeoutError` if it times out
 *
 * @example
 * ```ts
 * try {
 *   const data: string = await withTimeout(fetchUserData<string>(userId), 5000);
 * } catch (err) {
 *   if (err instanceof TimeoutError) {
 *     console.error('Request took too long');
 *   }
 * }
 * ```
 */
export const withTimeout = <T = void>(
  promise: Promise<T>,
  ms: number,
  reason = 'timeout',
): Promise<T> => Promise.race([promise, timeout<T>(ms, reason)])
